import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  MessageSquareText, Mail, Phone, Building2, Users, Clock, 
  CheckCircle, XCircle, CalendarCheck, Trash2, Filter, Play, Target 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'New', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40' },
  contacted: { label: 'Contacted', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/40' },
  demo_scheduled: { label: 'Demo Scheduled', bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/40' },
  converted: { label: 'Converted', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/40' },
  closed: { label: 'Closed', bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/40' },
};

export default function SuperAdminDemoRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [statusFilter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('superAdminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchRequests = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/demo-requests${params}`, { headers: getAuthHeaders() });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch demo requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/demo-requests/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/demo-requests/${requestId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Delete this demo request permanently?')) return;
    try {
      await axios.delete(`${API}/demo-requests/${requestId}`, { headers: getAuthHeaders() });
      toast.success('Demo request deleted');
      fetchRequests();
      fetchStats();
      setDetailOpen(false);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleConvertToLead = async (requestId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.post(`${API}/demo-requests/${requestId}/convert-to-lead`, {}, { headers: getAuthHeaders() });
      const leadId = res.data?.lead_id;
      toast.success(res.data?.message || 'Converted to CRM lead');
      fetchRequests();
      fetchStats();
      setDetailOpen(false);
      // Navigate to CRM and open the lead
      if (leadId) {
        window.location.href = `/super-admin/crm`;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    }
  };

  const viewDetail = (req) => {
    setSelectedRequest(req);
    setDetailOpen(true);
  };

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Demo Requests
        </h1>
        <p className="text-slate-400 mt-1">Manage incoming demo requests from the landing page</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-white' },
            { label: 'New', value: stats.new, color: 'text-blue-400' },
            { label: 'Contacted', value: stats.contacted, color: 'text-amber-400' },
            { label: 'Scheduled', value: stats.demo_scheduled, color: 'text-violet-400' },
            { label: 'Converted', value: stats.converted, color: 'text-emerald-400' },
            { label: 'Closed', value: stats.closed, color: 'text-slate-400' },
          ].map((s) => (
            <Card key={s.label} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filter */}
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-600 text-white" data-testid="demo-status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Requests</SelectItem>
                <SelectItem value="new" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">New</SelectItem>
                <SelectItem value="contacted" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Contacted</SelectItem>
                <SelectItem value="demo_scheduled" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Demo Scheduled</SelectItem>
                <SelectItem value="converted" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Converted</SelectItem>
                <SelectItem value="closed" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquareText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No demo requests yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead className="text-slate-400">Company</TableHead>
                  <TableHead className="text-slate-400">Team Size</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <TableRow key={req.id} className="border-slate-700 hover:bg-slate-800/50 cursor-pointer" onClick={() => viewDetail(req)}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{req.name}</p>
                        <p className="text-xs text-slate-500">{req.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">{req.company}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{req.team_size || '—'}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_CONFIG[req.status]?.bg} ${STATUS_CONFIG[req.status]?.text} ${STATUS_CONFIG[req.status]?.border} border`}>
                        {STATUS_CONFIG[req.status]?.label || req.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {new Date(req.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={req.status}
                        onValueChange={(v) => handleStatusUpdate(req.id, v)}
                      >
                        <SelectTrigger className="w-36 h-8 bg-slate-800 border-slate-600 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                          <SelectItem value="new" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">New</SelectItem>
                          <SelectItem value="contacted" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Contacted</SelectItem>
                          <SelectItem value="demo_scheduled" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Demo Scheduled</SelectItem>
                          <SelectItem value="converted" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Converted</SelectItem>
                          <SelectItem value="closed" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Demo Request Details</DialogTitle>
            <DialogDescription className="text-slate-400">
              Submitted {selectedRequest && new Date(selectedRequest.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500">Name</p>
                    <p className="text-white font-medium">{selectedRequest.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="text-white font-medium">{selectedRequest.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500">Company</p>
                    <p className="text-white font-medium">{selectedRequest.company}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="text-white font-medium">{selectedRequest.phone || '—'}</p>
                  </div>
                </div>
              </div>
              {selectedRequest.team_size && (
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-slate-500 mt-1" />
                  <div>
                    <p className="text-xs text-slate-500">Team Size</p>
                    <p className="text-white font-medium">{selectedRequest.team_size}</p>
                  </div>
                </div>
              )}
              {selectedRequest.message && (
                <div className="border-t border-slate-700 pt-3">
                  <p className="text-xs text-slate-500 mb-1">Message</p>
                  <p className="text-slate-300 text-sm leading-relaxed">{selectedRequest.message}</p>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-slate-700 pt-3">
                <Select
                  value={selectedRequest.status}
                  onValueChange={(v) => {
                    handleStatusUpdate(selectedRequest.id, v);
                    setSelectedRequest({ ...selectedRequest, status: v });
                  }}
                >
                  <SelectTrigger className="w-44 bg-slate-800 border-slate-600 text-white" data-testid="demo-detail-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                    <SelectItem value="new" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">New</SelectItem>
                    <SelectItem value="contacted" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Contacted</SelectItem>
                    <SelectItem value="demo_scheduled" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Demo Scheduled</SelectItem>
                    <SelectItem value="converted" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Converted</SelectItem>
                    <SelectItem value="closed" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  {!selectedRequest.converted_lead_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      onClick={() => handleConvertToLead(selectedRequest.id)}
                      data-testid="convert-to-lead-btn"
                    >
                      <Target className="h-4 w-4 mr-1" /> Convert to CRM Lead
                    </Button>
                  )}
                  {selectedRequest.converted_lead_id && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border text-xs">
                      ✓ In CRM
                    </Badge>
                  )}
                  {!selectedRequest.demo_slug && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                      onClick={() => {
                        setDetailOpen(false);
                        navigate(`/super-admin/demo-portals?provision=true&company=${encodeURIComponent(selectedRequest.company)}&name=${encodeURIComponent(selectedRequest.name)}&email=${encodeURIComponent(selectedRequest.email)}&requestId=${selectedRequest.id}`);
                      }}
                      data-testid="provision-demo-from-request-btn"
                    >
                      <Play className="h-4 w-4 mr-1" /> Provision Demo
                    </Button>
                  )}
                  {selectedRequest.demo_slug && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border text-xs">
                      Demo: {selectedRequest.demo_slug}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => handleDelete(selectedRequest.id)}
                    data-testid="demo-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
